import { inject, Injectable } from '@angular/core';
import { ReplenishmentDto, WarehouseDto } from '@pf/platform-wms/domain';
import { sumBy, uniqBy } from 'lodash-es';
import { CpwTaskStateService } from '../infrastructure/cpw-task-state.service';
import { HashObject } from '@pf/shared-utility';
import { ReplenishmentSummary } from '../models/replenishment-summary.model';
import { ReplenishmentTask } from '../models/replenishment-task.model';
import { ReplenishmentSummaryFactory } from '../factories/replenishment-summary.factory';
import {
    EquipmentGroup,
    EquipmentGroupIcons,
} from '../models/cpw-equipment-types';

const DEFAULT_BATCH_SIZE = 20;

@Injectable()
export class ReplenishmentTaskOrchestrator {
    private readonly taskStateService = inject(CpwTaskStateService);
    private readonly replenishmentSummaryFactory = inject(
        ReplenishmentSummaryFactory
    );

    orchestrate(
        warehouse: WarehouseDto,
        pendingReplenishments: ReplenishmentDto[],
        activeReplenishments: ReplenishmentDto[],
        showLtlProjects: boolean
    ) {
        const warehouseId = warehouse.id;
        const batchSize =
            warehouse.settings?.replenishmentBatchSize ?? DEFAULT_BATCH_SIZE;

        const pendingReplenishmentSummaries = pendingReplenishments
            .sort((a, b) => {
                const aEndDate = new Date(a.createdDate as string);
                const bEndDate = new Date(b.createdDate as string);
                if (aEndDate < bEndDate) return -1;
                if (aEndDate > bEndDate) return 1;
                return new Date(a.createdDate as string) <
                    new Date(b.createdDate as string)
                    ? -1
                    : 1;
            })
            .map(replenishment =>
                this.replenishmentSummaryFactory.create(replenishment)
            )
            .filter(summary => showLtlProjects || !summary.isLtl);

        const activeReplenishmentSummaries = activeReplenishments.map(
            replenishment =>
                this.replenishmentSummaryFactory.create(replenishment)
        );

        const pendingTasks = this.getReplenishmentGroups(
            warehouseId,
            batchSize,
            pendingReplenishmentSummaries
        );
        const replenishingTasks = this.getReplenishmentTasks(
            warehouseId,
            batchSize,
            'replenishing',
            false,
            [
                [
                    this.groupByEquipmentType(activeReplenishmentSummaries)
                        .keys()
                        .next().value || 'Parts',
                    activeReplenishmentSummaries,
                ],
            ]
        );

        if (activeReplenishmentSummaries.length > 0) {
            pendingTasks.forEach(task => {
                task.readonly = true;
            });

            replenishingTasks.forEach(task => {
                task.claimedAt = activeReplenishments[0].modifiedDate;
            });
        }

        this.taskStateService.setTasks([...pendingTasks, ...replenishingTasks]);
    }

    private getGroup(summary: ReplenishmentSummary): EquipmentGroup {
        return summary.isLtl ? 'LTL' : 'Parts';
    }

    private groupByEquipmentType(replenishments: ReplenishmentSummary[]) {
        return replenishments.reduce((pv, summary) => {
            const groupName = this.getGroup(summary);
            if (!pv.has(groupName)) pv.set(groupName, []);
            pv.get(groupName)?.push(summary);
            return pv;
        }, new Map<EquipmentGroup, ReplenishmentSummary[]>());
    }

    private getReplenishmentGroups(
        warehouseId: string,
        batchSize: number,
        replenishmentSummaries: ReplenishmentSummary[]
    ) {
        const grouped = this.groupByEquipmentType(replenishmentSummaries);

        return this.getReplenishmentTasks(
            warehouseId,
            batchSize,
            'pending',
            false,
            Array.from(grouped.entries())
        );
    }

    private getReplenishmentTasks(
        warehouseId: string,
        batchSize: number,
        section: ReplenishmentTask['section'],
        priority: boolean,
        groups: [EquipmentGroup, ReplenishmentSummary[]][]
    ) {
        const tasks: ReplenishmentTask[] = [];
        groups.forEach(([group, replenishments]) => {
            replenishments
                .reduce((buckets, replenishment, index) => {
                    const groupIndex = Math.floor(index / batchSize);
                    if (!buckets[groupIndex]) buckets[groupIndex] = [];
                    buckets[groupIndex].push(replenishment);
                    return buckets;
                }, [] as ReplenishmentSummary[][])
                .forEach(bucket => {
                    const task = this.getReplenishmentTask(
                        warehouseId,
                        section,
                        priority,
                        bucket,
                        group
                    );
                    tasks.push(task);
                });
        });

        return tasks;
    }

    private getReplenishmentTask(
        warehouseId: string,
        section: ReplenishmentTask['section'],
        priority: boolean,
        summaries: ReplenishmentSummary[],
        group: EquipmentGroup
    ) {
        const title =
            summaries.length > 1
                ? `${summaries.length} Replenishments`
                : '1 Replenishment';
        const totalItems = sumBy(summaries, x => x.totalQuantityToReplenish);
        const zones = uniqBy(
            summaries.map(summary => [summary.fromZone, summary.toZone]).flat(),
            x => x.id
        );
        const id = HashObject(summaries.map(x => x.replenishmentId).sort());
        return {
            id,
            warehouseId,
            section,
            title,
            details: `${totalItems} items - Zones: ${zones.length}`,
            time: summaries[0].createdDate,
            priority,
            taskIconType: EquipmentGroupIcons[group],
            group: group,
            replenishmentSummaries: summaries,
            readonly: false,
            isLtl: group === 'LTL',
        } as ReplenishmentTask;
    }
}
